import { Search16Icon } from '@jablotron/lib.spa-icons/icons/search-16';
import { type ForwardedRef, forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

type SearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  /** Classes for the positioned wrapper — width, flex sizing, margins. */
  className?: string;
  /** Absolutely positioned content at the right edge, e.g. a clear button; the input reserves room for it. */
  trailing?: ReactNode;
};

/**
 * Compact search field with a leading magnifier: light surface fill, subtle border, and a cyan border
 * plus soft ring on focus. 32 px high like every other control in a filter row. Used for the header
 * catalog search and the in-page list filters.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className = '', trailing, ...inputProps }: SearchInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  return (
    <div className={`relative ${className}`}>
      <Search16Icon
        width={14}
        height={14}
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brand-subtle"
      />
      <input
        ref={ref}
        {...inputProps}
        className={`h-8 w-full rounded-lg border border-primary bg-brand-surface py-0 pl-9 text-sm text-brand-ink outline-none transition-shadow focus:border-brand-cyan focus:shadow-[0_0_0_2px_#0085cf] ${
          trailing ? 'pr-9' : 'pr-3'
        }`}
      />
      {trailing}
    </div>
  );
});
